import { useCallback, useEffect, useMemo, useState } from 'react';
import notify from '../../../utils/notify';
import useNegotiationApi from './useNegotiationApi';
import { getPeriodLabel } from '../utils/negotiationList';
import { PACKAGE_STATUS } from '../utils/negotiationStatus';

const getDistinctOptions = values =>
  [...new Set(values.filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b)))
    .map(value => ({ value, label: value }));

const useNegotiationList = ({
  endpoint = '/negotiation',
  productTab = 'product-hydro',
  refreshTrigger = 0,
} = {}) => {
  const { listPackages } = useNegotiationApi();
  const [rawItems, setRawItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    try {
      setRawItems(await listPackages({ endpoint }));
    } catch (error) {
      notify.apiError(error, 'load negotiation data');
      setRawItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, listPackages]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshTrigger]);

  // Rows of the active product tab, before search and status filters.
  const tabItems = useMemo(() => {
    if (productTab === 'product-hydro') {
      return rawItems.filter(
        i => String(i.productName || '').toUpperCase() !== 'LPG'
      );
    }
    if (productTab === 'hydrocarbon-condensat') return [];
    return rawItems;
  }, [rawItems, productTab]);

  const filtered = useMemo(() => {
    let items = tabItems;

    if (searchText) {
      const q = searchText.toLowerCase();
      items = items.filter(i =>
        [
          i.procurementNumber,
          i.titleProcurement,
          i.productName,
          getPeriodLabel(i),
        ].some(value =>
          String(value || '')
            .toLowerCase()
            .includes(q)
        )
      );
    }

    if (statusFilter) {
      items = items.filter(i => i.statusLabel === statusFilter);
    }

    if (periodFilter) {
      items = items.filter(i => getPeriodLabel(i) === periodFilter);
    }

    return items;
  }, [tabItems, searchText, statusFilter, periodFilter]);

  // Distinct values of the loaded rows, so the select only offers what exists.
  const periodOptions = useMemo(
    () => getDistinctOptions(tabItems.map(getPeriodLabel)),
    [tabItems]
  );

  const statusCounts = useMemo(() => {
    const count = status =>
      tabItems.filter(i => i.statusLabel === status).length;
    return {
      total: tabItems.length,
      [PACKAGE_STATUS.OPEN]: count(PACKAGE_STATUS.OPEN),
      [PACKAGE_STATUS.IN_PROGRESS]: count(PACKAGE_STATUS.IN_PROGRESS),
      [PACKAGE_STATUS.DONE]: count(PACKAGE_STATUS.DONE),
    };
  }, [tabItems]);

  const totalRows = filtered.length;
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, currentPage, rowsPerPage]);

  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = value => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePeriodFilter = value => {
    setPeriodFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = page => setCurrentPage(page);

  const handlePerRowsChange = perPage => {
    setRowsPerPage(perPage);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('');
    setPeriodFilter('');
    setCurrentPage(1);
  };

  return {
    data: { items: pageItems },
    allItems: filtered,
    statusCounts,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    statusFilter,
    periodFilter,
    periodOptions,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handlePeriodFilter,
    handlePageChange,
    handlePerRowsChange,
    clearFilters,
    refresh: fetchList,
  };
};

export default useNegotiationList;
